import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, OnDestroy, ViewChild, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, ValidatorFn } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { pluck } from 'rxjs/operators';

import { Router } from '@angular/router';
import { NgOtpInputModule } from "ng-otp-input";
import { ButtonMdComponent } from "src/app/components/button-md/button-md.component";
import { InputComponent } from "src/app/components/input/input.component";
import { IMAGE_URLS } from 'src/app/core/constants/image-source';
import * as HTML_CONSTANTS from 'src/app/core/constants/login-constants';
import { OAUTH_PROVIDER_CHOICES } from 'src/app/core/constants/provider.constants';
import { CustomValidators } from 'src/app/core/constants/validators';
import { AdsManagementService } from 'src/app/core/services/adsManagement/ads-management.service';
import { AddToCart } from 'src/app/core/services/cart/cart-api.service';
import { CartRepositoryService } from 'src/app/core/services/cart/cart-repository.service';
import { ConversionTrackingService } from 'src/app/core/services/conversion/conversion-tracking.service';
import { NotificationService } from 'src/app/core/services/notification/notification.service';
import { MyOverlayRef } from 'src/app/core/services/overlay/myoverlay-ref';
import { OverlayService } from 'src/app/core/services/overlay/overlay.service';
import { PendingCartService } from 'src/app/core/services/pending-cart/pending-cart.service';
import { UniversalWrapperService } from 'src/app/core/services/universal-wrapper/universal-wrapper.service';
import { LoginForm } from 'src/app/core/types/forms.types';
import { LoginOverlayStateService } from 'src/app/products/components/login-overlay-state/login-overlay-state.service';
import { AuthenticationRepositoryService } from '../../services/authentication-repository.service';
import { ForgotPasswordComponent } from '../forgot-password/forgot-password.component';
import { RegistrationComponent } from "../registration/registration.component";

export interface LoginSuccess {
  success: boolean;
}

@Component({
  standalone: true,
  selector: 'webstore-overlay-login',
  templateUrl: './overlay-login.component.html',
  styleUrls: ['./overlay-login.component.scss'],
  imports: [CommonModule, ReactiveFormsModule, InputComponent, ButtonMdComponent, NgOtpInputModule, RegistrationComponent]
})
export class OverlayLoginComponent implements AfterViewInit, OnDestroy {
  private fb = inject(FormBuilder);
  private authenticationRepositoryService = inject(AuthenticationRepositoryService);
  private cartRepositoryService = inject(CartRepositoryService);
  private overlayService = inject(OverlayService);
  ref = inject<MyOverlayRef<OverlayLoginComponent, LoginSuccess>>(MyOverlayRef);
  private universalWrapperService = inject(UniversalWrapperService);
  private notificationService = inject(NotificationService);
  private conversionTrackingService = inject(ConversionTrackingService);
  private adsManagementService = inject(AdsManagementService);
  private route = inject(ActivatedRoute);
  private loginOverlayStateService = inject(LoginOverlayStateService);
  private router = inject(Router);
  private pendingCartService = inject(PendingCartService);

  @ViewChild('overlayContainer') overlayContainer!: ElementRef;
  IMAGE_URLS = IMAGE_URLS;

  loginForm: FormGroup<LoginForm> = this.fb.group<LoginForm>({
    login_credential: this.fb.control('', [CustomValidators.required, CustomValidators.loginCredential]),
    password: this.fb.control('', [CustomValidators.required])
  });

  login_credential = this.fb.control('', [CustomValidators.required, CustomValidators.email].filter(Boolean) as ValidatorFn[]);

  private forgotPasswordTimeoutId: ReturnType<typeof setTimeout> | null = null;

  get loginCredentialControl() {
    return this.loginForm.controls.login_credential;
  }

  get passwordControl() {
    return this.loginForm.controls.password;
  }

  get emailAddress() {
    if (this.loginWithOtp) {
      return this.login_credential?.valid ? this.login_credential.value : "";
    }
    return this.loginCredentialControl?.valid ? this.loginCredentialControl.value : "";
  }

  OTP_CONFIG = {
    length: 6,
    allowNumbersOnly: true,
    containerClass: 'otp-container',
    inputClass: 'otp-input'
  };
  showSignInFlow = false;
  otp = this.fb.control('', { updateOn: 'blur', validators: CustomValidators.otp });
  showOTPInput = false;

  forgotPasswordRef!: MyOverlayRef<ForgotPasswordComponent>;
  buttonLoading = false;
  otpButtonLoading = false;
  loginWithOtp = true;
  resendButtonLoading = false;
  enterTop = false;

  HTML_CONSTANTS = HTML_CONSTANTS;

  errorMessage = '';

  constructor() {
    // Service auto-initializes when injected (Angular 20 pattern)
  }

  ngAfterViewInit(): void {
    this.loginOverlayStateService.openOverlay();

    // No need to clear pending cart on page load - preserve it for authentication
  }

  async login() {
    this.loginForm.markAllAsTouched();
    if (this.loginForm.valid) {
      this.buttonLoading = true;
      const formValue = this.loginForm.getRawValue();
      let login_credential = formValue.login_credential || '';
      let password = formValue.password || '';
      // create model to handle reqBody
      login_credential = login_credential.trim();
      // Get UTM and code details for attribution tracking
      const collectedUtmAndCodeDetails = this.adsManagementService.getCollectedUtmAndCodeDetails();
      const reqObj = {
        username: login_credential,
        password,
        provider: OAUTH_PROVIDER_CHOICES.consumer,
        ...(collectedUtmAndCodeDetails && Object.keys(collectedUtmAndCodeDetails)?.length && {
          attribution_data: {
            ...collectedUtmAndCodeDetails
          }
        })
      };

      try {
        await this.authenticationRepositoryService.login(reqObj);
        // inject ga script
        this.conversionTrackingService.trackLoginConversion();

        // Handle pending cart and navigation
        this.handlePendingCart();
      } catch (error) {
        if (error instanceof HttpErrorResponse) {
          this.errorMessage = error?.error?.message;
        } else {
          this.errorMessage = 'Login failed. Please try again.';
        }
      }

      this.buttonLoading = false;
    }
  }

  changeLoginOption() {
    this.loginWithOtp = !this.loginWithOtp;
    const userName = this.loginForm?.get("login_credential");

    // Patching the valid email on login with password and login with otp email controls
    if (!this.loginWithOtp && this.login_credential?.valid) {
      userName?.patchValue(this.login_credential?.value);
    }
    if (this.loginWithOtp && userName?.valid) {
      this.login_credential?.patchValue(userName?.value);
    }
  }

  async sendOTP(login_credential: string) {
    try {
      await this.authenticationRepositoryService.sendOTP(login_credential);
      this.notificationService.showSuccess('OTP Sent');
      this.authenticationRepositoryService.setCurrentLoginCredential(login_credential);
      this.showOTPInput = true;
    } catch (error) {
      if (error instanceof HttpErrorResponse) {
        this.errorMessage = error?.error?.message;
      } else {
        this.errorMessage = 'Failed to send OTP. Please try again.';
      }
    }

    this.buttonLoading = false;
  }

  async requestOTP() {
    this.login_credential.markAllAsTouched();
    if (this.login_credential.valid && this.login_credential.value) {
      this.buttonLoading = true;
      await this.sendOTP(this.login_credential.value);
    }
  }



  onOtpChange(otp: string): void {
    this.otp.setValue(otp);
  }


  async verifyOTP() {
    this.otp.markAllAsTouched();
    if (this.otp.valid) {
      this.buttonLoading = true;

      try {
        await this.authenticationRepositoryService.verifyOTP({ otp: this.otp.value || '' });
        // inject ga script
        this.conversionTrackingService.trackLoginConversion();

        // Handle pending cart and navigation
        this.handlePendingCart();
      } catch (error) {
        if (error instanceof HttpErrorResponse) {
          this.errorMessage = error?.error?.message;
        } else {
          this.errorMessage = 'OTP verification failed. Please try again.';
        }
      }

      this.buttonLoading = false;
    }
  }

  async resendOTP() {
    const currentLoginCredential = this.authenticationRepositoryService.currentLoginCredential();
    if (!currentLoginCredential) {
      this.notificationService.showError('Failed to resend OTP, Please try again.');
      return;
    }

    this.resendButtonLoading = true;

    try {
      await this.authenticationRepositoryService.sendOTP(currentLoginCredential);
      this.notificationService.showSuccess('OTP Sent');
    } catch (error) {
      if (error instanceof HttpErrorResponse) {
        this.errorMessage = error?.error?.message;
      } else {
        this.errorMessage = 'Failed to resend OTP. Please try again.';
      }
    }

    this.resendButtonLoading = false;
  }




  forgotPassword() {

    if (this?.ref) {
      this.ref.close({ success: false });
    }
    this.forgotPasswordRef = this.overlayService.open(ForgotPasswordComponent, null, true, 'centered', 'forget-password-overlay', 'block-scroll');

    // Subscribe to success response


    this.forgotPasswordRef.receiveData$.pipe(pluck('data')).subscribe({
      next: (success) => {
        if (success) {
          this.forgotPasswordTimeoutId = setTimeout(() => {
            this.forgotPasswordRef.close();
            this.universalWrapperService.getWindow()?.location.reload();
          }, 2000);
        }
      },
      error: () => {
      }
    });
  }

  createAccount() {
    this.showSignInFlow = true;
    this.overlayContainer.nativeElement.scrollTo({ top: 0, behavior: 'smooth' });
  }

  completeRegistration() {
    this.cartRepositoryService.loadCart();
    this.buttonLoading = false;
    this.ref.close({ success: true });
  }

  goToLogin() {
    this.showSignInFlow = false;
  }

  ngOnDestroy(): void {
    this.conversionTrackingService.removeConversionTracking('Login');
    this.loginOverlayStateService.closeOverlay();
    // Clear timeout to prevent memory leaks
    if (this.forgotPasswordTimeoutId) {
      clearTimeout(this.forgotPasswordTimeoutId);
      this.forgotPasswordTimeoutId = null;
    }
  }

  private async handlePendingCart() {
    // Get pending product - validation is handled by getPendingProduct
    const pendingProduct = this.pendingCartService.getPendingProduct();
    if (!pendingProduct) {
      const returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';
      this.router.navigateByUrl(returnUrl);
      // Load cart only when there's no pending product
      this.cartRepositoryService.loadCart();
      return;
    }

    // Get UTM and code details for attribution tracking
    const collectedUtmAndCodeDetails = this.adsManagementService.getCollectedUtmAndCodeDetails();
    const cart: AddToCart = {
      cart_items: [{
        product: pendingProduct.id,
        quantity: pendingProduct.quantity,
        ...(pendingProduct.bid_price && pendingProduct.bid_price > 0 && {
          bid_price: pendingProduct.bid_price
        })
      }],
      ...(collectedUtmAndCodeDetails && Object.keys(collectedUtmAndCodeDetails)?.length && {
        attribution_data: {
          ...collectedUtmAndCodeDetails
        }
      })
    };

    try {
      await this.cartRepositoryService.addProductToCart(cart);
      // Get the updated cart from the service for tracking
      const updatedCart = this.cartRepositoryService.cart();
      if (updatedCart) {
        this.conversionTrackingService.trackAddToCartConversion(updatedCart);
      }
      this.notificationService.showSuccess('Product added to cart successfully.');
      this.pendingCartService.clearPendingProduct();
      // Navigate based on stayOnCurrentPage flag
      if (pendingProduct.stayOnCurrentPage) {
        const returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';
        this.router.navigateByUrl(returnUrl);
      } else {
        this.router.navigateByUrl('/cart');
      }
    } catch (error) {
      // Still navigate even if cart add fails
      const returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';
      this.router.navigateByUrl(returnUrl);
    }
  }


}

